import { useCallback, useEffect, useMemo, useState } from "react";
import { deviceApi } from "../../infrastructure/deviceApi";
import { diaLocal } from "../fecha";
import type { Sale } from "../../domain/types";

/**
 * Ventas de la terminal, filtradas por día en el cliente.
 *
 * En v1 el filtro era del servidor (`GET /sales?date=`). v2 devuelve las últimas N ventas
 * del tenant y no acepta rango: el corte por fecha se hace acá. Para reportes por período
 * está `/admin` (manager), que sí agrega en el backend.
 */
export function useSales(date: string) {
  const [todas, setTodas] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await deviceApi.sales.list(200);
      setTodas(
        data.map((s) => ({
          id: s.order_id,
          payment_method: s.payment_method,
          total_mxn: Number(s.total_mxn),
          discount_mxn: Number(s.discount_mxn ?? 0),
          total_sats: s.amount_sats,
          exchange_rate: s.fx_rate === null ? null : Number(s.fx_rate),
          payment_hash: s.payment_hash,
          status: s.status,
          created_at: s.created_at,
          paid_at: s.paid_at,
          items: (s.items ?? []).map((i) => ({
            product_id: i.product_id,
            description: i.description,
            quantity: i.qty,
            unit_price_mxn: Number(i.unit_price_mxn),
            line_total_mxn: Number(i.line_total_mxn),
          })),
        }))
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error cargando las ventas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // El corte del día es por CUÁNDO ENTRÓ EL DINERO (`paid_at`), no por cuándo se armó el
  // carrito: una venta iniciada 23:58 y pagada 00:01 pertenece al día que se cobró. Y el día
  // es el LOCAL del comercio, no el UTC (ver application/fecha.ts).
  const sales = useMemo(
    () => todas.filter((s) => diaLocal(s.paid_at ?? s.created_at) === date),
    [todas, date]
  );

  const paid = sales.filter((s) => s.status === "paid");
  const totalMxn = paid.reduce((s, v) => s + v.total_mxn, 0);
  const totalSats = paid.reduce((s, v) => s + (v.total_sats ?? 0), 0);
  const totalEfectivo = paid
    .filter((s) => s.payment_method === "cash")
    .reduce((s, v) => s + v.total_mxn, 0);

  return { sales, loading, error, totalMxn, totalSats, totalEfectivo, count: paid.length, refresh };
}
